import { useState, useMemo, useEffect } from "react";
import { StatusBadge, money, fmtDate } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { ArrowUpDown, Check, X, Pencil, Trash2, Search, Download, PauseCircle, Archive, RotateCcw } from "lucide-react";

const COLS = [
  { key: "title", label: "Deal" },
  { key: "retailer", label: "Retailer" },
  { key: "category", label: "Category" },
  { key: "deal_type", label: "Type" },
  { key: "scope", label: "Where" },
  { key: "original_price", label: "Orig", num: true },
  { key: "deal_price", label: "Deal", num: true },
  { key: "status", label: "Status" },
  { key: "source", label: "Source" },
  { key: "created_at", label: "Created" },
  { key: "expires_at", label: "Ends" },
];

const scopeText = (d) =>
  d.location_scope === "city" ? `${d.city || "?"}, ${d.province || ""}`.trim().replace(/,\s*$/, "")
  : d.location_scope === "province" ? (d.province || "Province")
  : "Canada-wide";

// Spreadsheet-style view of ALL deals with sort + filter + inline approve.
export const AllDealsTab = ({ deals, approveSub, rejectSub, openEdit, deleteDeal, bulkDeleteDeals,
  bulkCloseDeals, bulkApproveDeals, bulkRejectDeals, unarchiveDeal, fetchArchivedDeals,
  cleanupForm, setCleanupForm, saveCleanupSettings, cleanupStats }) => {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [cat, setCat] = useState("all");
  const [sortKey, setSortKey] = useState("created_at");
  const [dir, setDir] = useState("desc");
  const [selected, setSelected] = useState(() => new Set());
  const [archived, setArchived] = useState(null);

  useEffect(() => {
    if (status === "archived" && archived === null && fetchArchivedDeals) {
      fetchArchivedDeals().then(setArchived).catch(() => setArchived([]));
    }
  }, [status, archived, fetchArchivedDeals]);

  const sourceDeals = status === "archived" ? (archived || []) : deals;

  const categories = useMemo(
    () => Array.from(new Set(sourceDeals.map((d) => d.category).filter(Boolean))).sort(),
    [sourceDeals]
  );

  const rows = useMemo(() => {
    let r = sourceDeals.filter((d) => {
      if (status !== "all" && (d.status || "active") !== status) return false;
      if (cat !== "all" && d.category !== cat) return false;
      if (q) {
        const hay = `${d.title} ${d.retailer} ${d.source || ""} ${d.city || ""} ${d.province || ""}`.toLowerCase();
        if (!hay.includes(q.toLowerCase())) return false;
      }
      return true;
    });
    const val = (d) => (sortKey === "scope" ? scopeText(d) : d[sortKey]);
    r = [...r].sort((a, b) => {
      let av = val(a), bv = val(b);
      if (av == null) av = "";
      if (bv == null) bv = "";
      if (typeof av === "number" && typeof bv === "number") return dir === "asc" ? av - bv : bv - av;
      return dir === "asc" ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
    return r;
  }, [sourceDeals, q, status, cat, sortKey, dir]);

  const toggleSort = (key) => {
    if (sortKey === key) setDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setDir("asc"); }
  };

  const visibleIds = useMemo(() => rows.map((d) => d.deal_id), [rows]);
  const selectedVisible = visibleIds.filter((id) => selected.has(id));
  const allVisibleSelected = visibleIds.length > 0 && selectedVisible.length === visibleIds.length;

  const toggleRow = (id) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const toggleAllVisible = () => setSelected((prev) => {
    const next = new Set(prev);
    if (allVisibleSelected) visibleIds.forEach((id) => next.delete(id));
    else visibleIds.forEach((id) => next.add(id));
    return next;
  });
  const clearSelection = () => setSelected(new Set());
  const selectedPending = useMemo(() => {
    const byId = new Map(sourceDeals.map((d) => [d.deal_id, d]));
    return Array.from(selected).filter((id) => (byId.get(id)?.status) === "pending");
  }, [selected, sourceDeals]);
  const runBulkDelete = async () => {
    await bulkDeleteDeals(Array.from(selected));
    clearSelection();
    setArchived(null);
  };
  const runBulkClose = async () => {
    await bulkCloseDeals(Array.from(selected));
    clearSelection();
  };
  const runBulkApprove = async () => {
    await bulkApproveDeals(selectedPending);
    clearSelection();
  };
  const runBulkReject = async () => {
    await bulkRejectDeals(selectedPending);
    clearSelection();
  };
  const runUnarchive = async (d) => {
    await unarchiveDeal(d);
    setArchived(null);
  };

  const downloadCsv = () => {
    const headers = ["Deal", "Retailer", "Category", "Type", "Where", "Original", "Deal price", "Status", "Source", "Created", "Ends"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [headers.map(esc).join(",")];
    rows.forEach((d) => {
      lines.push([
        d.title, d.retailer, d.category, d.deal_type, scopeText(d),
        d.original_price != null ? Number(d.original_price).toFixed(2) : "",
        d.price_type === "tbc" ? "TBC" : Number(d.deal_price || 0).toFixed(2),
        d.status || "active", d.source || "", fmtDate(d.created_at), fmtDate(d.expires_at),
      ].map(esc).join(","));
    });
    const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `bogoshare_deals_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const pending = deals.filter((d) => (d.status || "") === "pending").length;

  return (
    <div>
      {cleanupForm && (
        <div data-testid="cleanup-settings" className="mb-4 flex flex-wrap items-center gap-4 rounded-lg border border-black/10 bg-secondary/30 px-4 py-3">
          <div className="flex items-center gap-2">
            <Archive className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Auto-cleanup of closed deals</span>
          </div>
          <div className="flex items-center gap-2">
            <Switch data-testid="cleanup-enabled" checked={cleanupForm.enabled}
              onCheckedChange={(v) => setCleanupForm((p) => ({ ...p, enabled: v }))} />
            <span className="text-xs text-muted-foreground">{cleanupForm.enabled ? "On" : "Off"}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Every</span>
            <Input data-testid="cleanup-interval" type="number" min="1" max="1440"
              value={cleanupForm.interval_minutes}
              onChange={(e) => setCleanupForm((p) => ({ ...p, interval_minutes: e.target.value }))}
              className="h-8 w-20" />
            <span className="text-xs text-muted-foreground">min</span>
          </div>
          <Button size="sm" data-testid="cleanup-save" onClick={saveCleanupSettings} className="h-8 rounded-full">
            Save
          </Button>
          {cleanupStats && (
            <span data-testid="cleanup-stats" className="text-xs font-medium text-muted-foreground">
              {cleanupStats.archived} archived · {cleanupStats.autoCleaned} auto-cleaned so far
            </span>
          )}
          <span className="text-[11px] text-muted-foreground basis-full sm:basis-auto sm:ml-auto sm:max-w-md">
            Closed deals with no partner history are deleted; those with completed matches are archived (records kept).
          </span>
        </div>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input data-testid="alldeals-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search deals, retailer, city…" className="pl-8 w-64" />
        </div>
        <select data-testid="alldeals-status" value={status} onChange={(e) => setStatus(e.target.value)}
          className="h-10 rounded-md border border-input bg-background px-3 text-sm">
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="pending">Pending{pending ? ` (${pending})` : ""}</option>
          <option value="closed">Closed</option>
          <option value="rejected">Rejected</option>
          <option value="archived">Archived</option>
        </select>
        <select data-testid="alldeals-category" value={cat} onChange={(e) => setCat(e.target.value)}
          className="h-10 rounded-md border border-input bg-background px-3 text-sm">
          <option value="all">All categories</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <span className="ml-auto text-xs text-muted-foreground" data-testid="alldeals-count">
          {rows.length} of {sourceDeals.length} deals
        </span>
        <Button size="sm" variant="outline" data-testid="alldeals-download" onClick={downloadCsv}
          className="h-10 rounded-full border-black/20">
          <Download className="mr-1 h-4 w-4" /> Download CSV
        </Button>
      </div>

      {selected.size > 0 && (
        <div data-testid="alldeals-bulk-bar"
          className="mb-3 flex flex-wrap items-center gap-3 rounded-lg border border-primary/30 bg-primary/5 px-4 py-2.5">
          <span className="text-sm font-medium" data-testid="alldeals-selected-count">
            {selected.size} deal{selected.size > 1 ? "s" : ""} selected
          </span>
          {selectedPending.length > 0 && (
            <>
              <Button size="sm" data-testid="alldeals-bulk-approve"
                onClick={runBulkApprove} className="h-8 rounded-full bg-accent hover:bg-accent/90">
                <Check className="mr-1 h-3.5 w-3.5" /> Approve {selectedPending.length} pending
              </Button>
              <Button size="sm" variant="outline" data-testid="alldeals-bulk-reject"
                onClick={runBulkReject} className="h-8 rounded-full border-black/20 text-destructive">
                <X className="mr-1 h-3.5 w-3.5" /> Reject {selectedPending.length} pending
              </Button>
            </>
          )}
          {status !== "archived" && (
            <Button size="sm" variant="outline" data-testid="alldeals-bulk-close"
              onClick={runBulkClose} className="h-8 rounded-full border-black/20">
              <PauseCircle className="mr-1 h-3.5 w-3.5" /> Close selected
            </Button>
          )}
          <Button size="sm" variant="destructive" data-testid="alldeals-bulk-delete"
            onClick={runBulkDelete} className="h-8 rounded-full">
            <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete selected
          </Button>
          <Button size="sm" variant="ghost" data-testid="alldeals-clear-selection"
            onClick={clearSelection} className="h-8 rounded-full">
            Clear
          </Button>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-black/10">
        <table className="w-full text-sm" data-testid="alldeals-table">
          <thead className="bg-secondary/50 text-left text-xs text-muted-foreground">
            <tr>
              <th className="p-2.5 w-10">
                <Checkbox data-testid="alldeals-select-all" checked={allVisibleSelected}
                  onCheckedChange={toggleAllVisible} aria-label="Select all deals" />
              </th>
              {COLS.map((c) => (
                <th key={c.key} className="p-2.5 whitespace-nowrap">
                  <button data-testid={`alldeals-sort-${c.key}`} onClick={() => toggleSort(c.key)}
                    className="inline-flex items-center gap-1 hover:text-foreground">
                    {c.label}
                    <ArrowUpDown className={`h-3 w-3 ${sortKey === c.key ? "text-foreground" : "opacity-40"}`} />
                  </button>
                </th>
              ))}
              <th className="p-2.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {rows.length === 0 ? (
              <tr><td colSpan={COLS.length + 2} className="p-4 text-center text-muted-foreground">No deals match.</td></tr>
            ) : rows.map((d) => (
              <tr key={d.deal_id} data-testid={`alldeals-row-${d.deal_id}`}
                className={`hover:bg-secondary/30 ${selected.has(d.deal_id) ? "bg-blue-50/60" : ""}`}>
                <td className="p-2.5">
                  <Checkbox data-testid={`alldeals-select-${d.deal_id}`} checked={selected.has(d.deal_id)}
                    onCheckedChange={() => toggleRow(d.deal_id)} aria-label={`Select ${d.title}`} />
                </td>
                <td className="p-2.5 max-w-[220px] truncate font-medium">{d.title}</td>
                <td className="p-2.5 whitespace-nowrap">{d.retailer}</td>
                <td className="p-2.5 whitespace-nowrap">{d.category}</td>
                <td className="p-2.5 max-w-[140px] truncate text-muted-foreground">{d.deal_type}</td>
                <td className="p-2.5 whitespace-nowrap text-muted-foreground">{scopeText(d)}</td>
                <td className="p-2.5 text-right whitespace-nowrap">{d.original_price != null ? money(d.original_price) : "—"}</td>
                <td className="p-2.5 text-right whitespace-nowrap">{d.price_type === "tbc" ? "TBC" : money(d.deal_price)}</td>
                <td className="p-2.5"><StatusBadge status={d.status} /></td>
                <td className="p-2.5 max-w-[160px] truncate text-xs text-muted-foreground">{d.source || "—"}</td>
                <td className="p-2.5 whitespace-nowrap text-xs text-muted-foreground">{fmtDate(d.created_at)}</td>
                <td className="p-2.5 whitespace-nowrap text-xs text-muted-foreground">{fmtDate(d.expires_at)}</td>
                <td className="p-2.5">
                  <div className="flex items-center justify-end gap-1">
                    {(d.status || "") === "pending" && (
                      <>
                        <Button size="sm" data-testid={`alldeals-approve-${d.deal_id}`} onClick={() => approveSub(d.deal_id)} className="h-7 rounded-full px-2">
                          <Check className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="outline" data-testid={`alldeals-reject-${d.deal_id}`} onClick={() => rejectSub(d.deal_id)} className="h-7 rounded-full px-2 text-destructive">
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    )}
                    {(d.status || "") === "archived" && (
                      <Button size="sm" variant="outline" data-testid={`alldeals-unarchive-${d.deal_id}`} onClick={() => runUnarchive(d)} className="h-7 rounded-full px-2">
                        <RotateCcw className="mr-1 h-3.5 w-3.5" /> Unarchive
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" data-testid={`alldeals-edit-${d.deal_id}`} onClick={() => openEdit(d)} className="h-7 px-2">
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" data-testid={`alldeals-delete-${d.deal_id}`} onClick={() => deleteDeal(d)} className="h-7 px-2 text-destructive hover:bg-destructive/10">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AllDealsTab;
